import * as React from 'react'
import type { Row, RowSelectionState, Table } from '@tanstack/react-table'

export interface SheetRowNavigation<TData> {
  selectedRowKey: string | undefined
  selectedRow: Row<TData> | undefined
  prevId: string | undefined
  nextId: string | undefined
  onPrev: () => void
  onNext: () => void
  onOpenChange: (isOpen: boolean) => void
}

/**
 * Prev/next row navigation for a detail sheet driven by a DataTable's row
 * selection. Walks the RENDERED row model (sorted/filtered), not core insertion
 * order, so ↑/↓ move to the next row as it appears on screen once a column is
 * sorted. Depending on the row model array (which is a fresh reference when data
 * arrives) also makes navigation recompute after an async load — e.g. a
 * deep-link that selects a row at mount before the filtered rows have loaded.
 *
 * Owns the document-level ArrowUp/ArrowDown handler (ignored while a menu is
 * focused) and the sheet's close handler, which restores focus to the
 * originating row after clearing the selection.
 *
 * Also owns recovery from a selection that can never resolve: a controlled
 * Radix `open` going back to `false` (X/ESC/backdrop) unmounts the sheet
 * without invoking `onOpenChange`, so `resetRowSelection` never runs on that
 * path. If the list query then errors, or the row gets filtered out, the
 * selection is left dangling — `rowSelection` still points at a row nothing
 * can show. That keeps a `details=1`-style URL flag armed (callers commonly
 * derive it from `rowSelection`), keeps the ArrowUp/ArrowDown handler below
 * armed on a row with no rendered index (swallowing the keys page-wide with
 * nothing to navigate), and pops the sheet back open unprompted as soon as
 * the row reappears in `data`. Once loading has settled with no matching row,
 * drop the selection so all three symptoms clear together.
 */
export function useSheetRowNavigation<TData>(
  table: Table<TData>,
  rowSelection: RowSelectionState,
  isLoading: boolean
): SheetRowNavigation<TData> {
  const selectedRowKey = Object.keys(rowSelection)[0]
  const rows = table.getRowModel().rows
  const index = React.useMemo(
    () => rows.findIndex((row) => row.id === selectedRowKey),
    [rows, selectedRowKey]
  )
  const selectedRow = index >= 0 ? rows[index] : undefined
  const prevId = index > 0 ? rows[index - 1]?.id : undefined
  const nextId = index >= 0 ? rows[index + 1]?.id : undefined

  React.useEffect(() => {
    if (selectedRowKey && !isLoading && !selectedRow) {
      table.resetRowSelection()
    }
  }, [selectedRowKey, isLoading, selectedRow, table])

  const onPrev = React.useCallback(() => {
    if (prevId) table.setRowSelection({ [prevId]: true })
  }, [prevId, table])
  const onNext = React.useCallback(() => {
    if (nextId) table.setRowSelection({ [nextId]: true })
  }, [nextId, table])

  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (!selectedRowKey) return
      // Let arrow keys drive an open dropdown/menu instead of the sheet.
      if (document.activeElement?.closest('[role="menu"]')) return
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        onPrev()
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        onNext()
      }
    }
    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [selectedRowKey, onPrev, onNext])

  const onOpenChange = React.useCallback(
    (isOpen: boolean) => {
      if (isOpen) return
      // Restore focus to the originating row on close. The sheet has no Trigger
      // element to return focus to, so grab the row by id before clearing the
      // selection, then focus it once the sheet has finished closing.
      const el = selectedRowKey ? document.getElementById(selectedRowKey) : null
      table.resetRowSelection()
      setTimeout(() => el?.focus(), 0)
    },
    [selectedRowKey, table]
  )

  return {
    selectedRowKey,
    selectedRow,
    prevId,
    nextId,
    onPrev,
    onNext,
    onOpenChange,
  }
}
